import * as Sentry from '@sentry/react'
import posthog from 'posthog-js'

const apiKey = import.meta.env.VITE_POSTHOG_KEY as string | undefined
// Default to a same-origin reverse proxy under a random-looking prefix so
// adblockers don't drop PostHog requests by path. Caddy proxies the prefix
// to us.i.posthog.com (`/static/*` → us-assets.i.posthog.com). Rotate the
// suffix here AND in infra/caddy/observability-tunnel.caddy together if
// filterlists ever start catching it. Use `||` (not `??`) so an empty
// VITE_POSTHOG_HOST from CI also falls back to the default.
const host =
    (import.meta.env.VITE_POSTHOG_HOST as string | undefined)?.trim() ||
    '/relay-2hd9'

let initialized = false

const PII_NETWORK_PATHS = [
    '/customers',
    '/sales-orders',
    '/payments',
    '/einvoice',
]

export function initPostHog() {
    if (initialized) return
    if (!apiKey) {
        console.warn('[PostHog] VITE_POSTHOG_KEY missing — analytics disabled')
        return
    }

    posthog.init(apiKey, {
        api_host: host,
        // Keep toolbar / replay links pointing at the real PostHog UI, even
        // though ingestion is tunneled through the same-origin proxy.
        ui_host: 'https://us.posthog.com',
        autocapture: false,
        capture_pageview: false,
        capture_pageleave: false,
        disable_session_recording: false,
        session_recording: {
            maskAllInputs: true,
            maskTextSelector:
                '[data-ph-mask], .ph-mask, .customer-name, .customer-phone, ' +
                '.customer-address, .price-display, .total-amount, ' +
                '.payment-amount, .discount-value, .invoice-number',
            maskCapturedNetworkRequestFn: (request) => {
                if (PII_NETWORK_PATHS.some((p) => request.name.includes(p))) {
                    request.requestBody = null
                    request.responseBody = null
                }
                return request
            },
            recordCrossOriginIframes: false,
        },
    })
    initialized = true
    console.info(`[PostHog] initialized → ${host}`)
}

export function identifyUser(user: { id: string; username: string }) {
    if (initialized) {
        posthog.identify(user.id, { username: user.username })
    }
    // Sentry.setUser is a no-op when Sentry isn't initialized, so it's safe
    // to call unconditionally — keeps Sentry events filterable by user.
    Sentry.setUser({ id: user.id, username: user.username })
}

export function resetPostHog() {
    if (initialized) posthog.reset()
    Sentry.setUser(null)
}

export { posthog }
